<script>
// Casca da navegação 2026. Espelha o contrato do <NavBar> (envolve a página via
// <slot/>) e, como o app.vue não tem <v-app>, fornece o próprio <v-app> para o
// contexto Vuetify. Compõe sidebar de contexto + topbar + conteúdo.
import {
  SIDEBAR_WIDTH_OPEN,
  SIDEBAR_WIDTH_SMALL,
} from '@modules/navegacao-v2/constants/layout'
import { NAV_CONTEXT } from '@modules/navegacao-v2/store/state'
import { clienteAccentColor } from '@modules/navegacao-v2/utils/cliente-accent'
import { apiNotifications } from '@modules/notifications/api/apiNotifications'
import NavegacaoV2Sidebar from './navegacao-v2-sidebar.vue'
import NavTopbar from './topbar.vue'

export default {
  name: 'NavegacaoV2Shell',
  components: {
    NavegacaoV2Sidebar,
    NavTopbar,
  },
  props: {
    // Squeeze de um painel lateral (Jornada/Especialista Fee-Based — ver
    // main.vue) aplicado só no CONTEÚDO, não no shell inteiro: o topbar
    // (busca/data/avatar) precisa continuar indo até a borda direita da
    // tela, por trás do painel, em vez de parar onde ele começa. Valor CSS
    // pronto (ex.: '380px', 'clamp(420px, 33vw, 640px)') ou '' para nenhum
    // squeeze — quem decide o valor é o layout, este componente só aplica.
    contentPaddingRight: {
      type: String,
      default: '',
    },
    // Rota imersiva (visão de tela cheia do chat de IA): a sidebar de contexto
    // sai e o conteúdo encosta na borda esquerda. O topbar FICA — é o que
    // mantém busca, notificações e avatar alcançáveis.
    //
    // Prop, e não um terceiro braço no `main.vue` que troque o shell inteiro:
    // desmontar o shell a cada entrada refaria o fetch de notificações do
    // `mounted` e re-rodaria o `syncContextFromRoute` da sidebar, que em aba
    // sem sessionStorage cai na heurística de rota e troca o contexto de
    // cliente por Gestão sozinho.
    semSidebar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { collapsed: false, mobileOpen: false }
  },
  computed: {
    // Em tela pequena (smAndDown) a sidebar vira drawer off-canvas e o conteúdo
    // ocupa a largura toda.
    isMobile() {
      return !!(this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.smAndDown)
    },
    // A sidebar é position:fixed (fica acima do conteúdo das páginas, que foram
    // feitas para o v-main do Vuetify). No desktop o conteúdo é deslocado por
    // margin-left reativo à largura (264 aberta / 84 recolhida); no mobile, 0.
    mainStyle() {
      if (this.isMobile || this.semSidebar) return { marginLeft: 0 }
      const width = this.collapsed ? SIDEBAR_WIDTH_SMALL : SIDEBAR_WIDTH_OPEN
      return { marginLeft: `${width}px` }
    },
    context() {
      return this.$store.getters['navigation/context']
    },
    // Na imersiva (`semSidebar`) o contexto GLOBAL de cliente nunca deve
    // pintar a topbar nem a faixa de destaque — a `/ia` tem o PRÓPRIO
    // conceito de "cliente em foco" (store `aawz-intelligence-v2`), e o
    // global pode ter sido setado por engano por uma ação que abriu OUTRA
    // aba (ex.: `abrir_cliente` navegando em aba nova) sem que esta aba
    // tenha realmente saído da gestão. Cinto-e-suspensório: a causa raiz já
    // foi corrigida em `abrir-cliente.js` (só entra no contexto quando a
    // navegação é na MESMA aba), mas esta tela não deve depender de todo
    // chamador futuro acertar isso.
    contextoEfetivo() {
      if (this.semSidebar) return NAV_CONTEXT.GESTAO
      return this.context
    },
    isCliente() {
      return this.contextoEfetivo === NAV_CONTEXT.CLIENTE
    },
    client() {
      return this.$currentClient() || {}
    },
    // Cor da faixa de contexto: primária para cliente, cinza para lead.
    accentColor() {
      return clienteAccentColor(this.client)
    },
  },
  watch: {
    // Fecha o drawer mobile ao trocar de rota (após clicar num módulo).
    $route() {
      this.mobileOpen = false
    },
  },
  mounted() {
    // Carrega notificações (mesma fonte da nav antiga) — o nav-bar.vue, que fazia
    // isso, não é renderizado no modo v2.
    apiNotifications.carregar()
  },
  methods: {
    toggleMobile() {
      this.mobileOpen = !this.mobileOpen
    },
    closeMobile() {
      this.mobileOpen = false
    },
  },
}
</script>

<template>
  <v-app class="nav-v2-shell">
    <NavegacaoV2Sidebar
      v-if="!semSidebar"
      :collapsed="collapsed"
      :mobile="isMobile"
      :open="mobileOpen"
      @toggle="collapsed = !collapsed"
    />
    <div
      v-if="isMobile && mobileOpen"
      class="nav-v2-shell__backdrop"
      @click="closeMobile"
    />
    <main class="nav-v2-shell__main" :style="mainStyle">
      <NavTopbar
        :variant="contextoEfetivo"
        :client="client"
        :show-hamburger="isMobile"
        @toggle-menu="toggleMobile"
      />
      <div v-if="isCliente" class="nav-v2-shell__accent" :style="{ background: accentColor }" />
      <div
        class="nav-v2-shell__content"
        :style="{ paddingRight: contentPaddingRight || null }"
      >
        <slot />
      </div>
    </main>

    <!-- Nenhum modal é montado aqui. Tanto o cliente-360 do CRM (aberto pela
    sidebar na visão de cliente) quanto os do menu de usuário (Meus dados /
    Alterar senha / Upload de avatar) vivem no host único <GlobalModals> do
    main.vue, sempre montado fora do switch v1/v2. Todos são disparados por
    GlobalBus.$emit — declarar aqui montaria a 2ª instância e abriria dois
    diálogos empilhados no contexto v2 (há teste travando isso em
    global-modals.unit.js). A prontidão que a sidebar espera antes de emitir
    (waitClienteModalHostReady) é sinalizada pelo próprio crm-lead-steps.vue
    no created()/beforeDestroy() — ver cliente-modal-host.js. -->
  </v-app>
</template>

<style lang="sass" scoped>
// Altura fixa de viewport + conteúdo com scroll interno — espelha o
// comportamento do <v-content> original (o documento em si não rola; quem rola
// é a área de conteúdo). Sem isso o conteúdo mais alto que a tela fica inacessível.

// Clampa o <v-app> ao viewport. O body global é `min-height: 100vh` flex-column
// (design/_template.sass) e o .v-application--wrap do Vuetify é `min-height: 100vh`:
// sem este clamp, qualquer resíduo de altura (ex: wrapper de modal in-flow) empurra
// o body para além de 100vh e cria um scroll na janela. Só o __content deve rolar
// — mesmo modelo do v1, onde o v-container era o único dono do scroll.
.nav-v2-shell.v-application
  height: 100vh
  max-height: 100vh
  overflow: hidden
  // O .v-application do Vuetify tem fundo branco do tema por padrão, que aparece
  // na área de overscroll/scroll atrás do conteúdo. Alinha com __main/__content.
  background-color: #fafafa

// Scrim do drawer mobile (fica abaixo da sidebar z-index 70, acima do conteúdo).
.nav-v2-shell__backdrop
  position: fixed
  inset: 0
  z-index: 65
  background: rgba(3, 3, 9, 0.5)

.nav-v2-shell__main
  height: 100vh
  display: flex
  flex-direction: column
  overflow: hidden
  // Fundo #fafafa para a área de scroll/overscroll seguir a cor do conteúdo
  // (v2). Topbar e accent têm suas próprias cores, então não herdam esse fundo.
  background: #fafafa
  transition: margin-left 0.2s cubic-bezier(0.16, 0.84, 0.44, 1)

// Faixa de contexto logo abaixo do topbar (só no contexto cliente). A cor vem
// do :style (primária para cliente, cinza para lead). Fora da área de scroll.
.nav-v2-shell__accent
  flex-shrink: 0
  height: 3px

.nav-v2-shell__content
  flex: 1
  min-height: 0
  overflow-y: auto
  overflow-x: hidden
  // Superfície "sunken": conteúdo num cinza levíssimo (#FAFAFA) para os cards
  // brancos saltarem. Topbar segue branco. #FAFAFA é quase branco, então o notch
  // dos inputs outlined praticamente não aparece (ao contrário do #f7f7f8 antigo).
  background: #fafafa
  // Squeeze de painel lateral (prop content-padding-right, ver main.vue) —
  // transição sempre ativa, não condicionada a classe: o valor muda de ''
  // para o CSS pronto (ou vice-versa) via :style, e a propriedade já
  // presente aqui garante a animação nos dois sentidos.
  transition: padding-right 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)
</style>
